// @ts-check
import React from 'react';
import {
  Col,
  Container,
  Row,
  Card,
  CardImg,
  CardBody,
  CardText,
  CardTitle,
} from 'reactstrap';
import { Link } from 'gatsby';

import Image from '../components/Image';
import Layout from '../components/Layout';
import Testimonial from '../components/Testimonial';
import NyplLogo from '../components/NyplLogo';

import '../typedefs';

const RECT_LOGO_WIDTH = '7rem';

// WARNING: when you change this image, update the object-position according to its contents
const PartnerSpotlight = () => (
  <Card className="mx-auto border rounded p-0 bg-white d-flex flex-column my-4 flex-md-row">
    <CardImg
      src="/img/madison-pfc.jpg"
      alt="a group of people standing outside with a woman in front of a podium next to a police officer"
      className="mr-3 card-image"
      style={{
        objectFit: 'cover',
        objectPosition: 'top',
      }}
    />
    <CardBody className="p-4">
      <CardTitle tag="h3" className="mt-2">
        Partner Spotlight
      </CardTitle>
      <CardText className="mb-1">
        LVN has been helping the city of Madison, Wisconsin with their effort to
        hire a Chief of Police who understands and listens to community
        concerns.
      </CardText>
      <Link className="font-weight-bold" to="/PFC">
        Learn more
      </Link>
    </CardBody>
  </Card>
);

const HomePage = () => {
  return (
    <Layout title="Local Voices Network">
      <Container>
        <div className="top-section section">
          <div className="text-center">
            <h1 className="text-uppercase mb-4">
              Public conversations for a stronger democracy
            </h1>
          </div>
          <Row>
            <Col xs={{ size: 12, order: 1 }} lg={{ size: 5, order: 0 }}>
              <p>
                Local Voices Network (LVN) is devoted to engaging and amplifying
                under-heard voices to inform public understanding, policy
                development and decision-making.
              </p>
              <p>
                Our goal is to restore our civic fabric by fostering community
                conversation and elevating insights that are too often missed in
                mainstream and social media. To get there, we prioritize agency,
                inclusion, and connection.
              </p>
            </Col>
            <Col>
              <Image
                className="hero-image mb-lg-0 mb-4"
                image="/img/cortico_home.png"
                alt="a group of women sitting at a round table around the hearth, a round wooden speaker and recording device"
              />
            </Col>
          </Row>
        </div>
      </Container>
      <div className="section section-bg bg-light-purple">
        <Container>
          <Row>
            <Col xs="12">
              <div className="text-container mx-auto">
                <h2 className="h1 mb-4">How it works</h2>
                <p>
                  LVN partners use our conversation model to gather the complex
                  and nuanced stories of people who have historically been
                  underrepresented in policy making and the media. Our online
                  platform and analytics tools (powered by{' '}
                  <a href="https://cortico.ai">Cortico</a> technology) distill
                  key themes from recorded conversations, enabling our partners
                  to lift up important perspectives from real people.
                </p>
                <div className="text-center">
                  <Link className="btn btn-important btn-primary" to="/about">
                    Learn More
                  </Link>
                </div>
                <PartnerSpotlight />
                <p>
                  Collectively, the conversations in the network create an
                  infrastructure for a stronger democracy by linking community
                  conversations across geographies, identities and perspectives.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      <div className="section section-bg bg-white">
        <Container>
          <Row>
            <Col xs="12">
              <div className="text-container mx-auto">
                <h2 className="h1 mb-4">
                  Our partners share the impact of LVN
                </h2>
                <div>
                  <Testimonial
                    speakerName="Alia Stevenson"
                    title="Chief Program Officer"
                    workplace="Foundation for Black Women's Wellness"
                    image={
                      <Image
                        image="/img/ffbww-logo.png"
                        style={{
                          maxWidth: RECT_LOGO_WIDTH,
                          mixBlendMode: 'multiply',
                        }}
                      />
                    }
                  >
                    The information that we collected with LVN is so rich. It
                    helps us illuminate data points in a different way,
                    communicating through people's stories and experiences.
                  </Testimonial>
                  <Testimonial
                    speakerName="Katie Dean"
                    title="Executive Editor"
                    workplace="Cap Times"
                    image={
                      <Image
                        image="/img/ct_logo.png"
                        style={{
                          maxWidth: RECT_LOGO_WIDTH,
                          marginTop: '-2px',
                        }}
                      />
                    }
                  >
                    Having the ability to use this incredible technology is such
                    a boost for us to think about new ways to report, to
                    identify innovative ways to reach people and connect with
                    new audiences.
                  </Testimonial>
                  <Testimonial
                    speakerName="Erica Parker"
                    title="Associate Manager of Adult Programming"
                    workplace="New York Public Library"
                    image={
                      <NyplLogo
                        style={{ marginTop: '-1rem', marginRight: '-1.25rem' }}
                      />
                    }
                  >
                    One of the most exciting things about building this program
                    has been the spirit of experimentation we've all had. Branch
                    staff have really embraced figuring out what their intended
                    audience is for this program and getting to know their
                    community in the process.
                  </Testimonial>
                  <div className="text-center mt-4">
                    <a
                      className="btn btn-important btn-primary"
                      href="https://cortico.ai/#partners"
                    >
                      Learn More
                    </a>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </Layout>
  );
};

export default HomePage;
